import {
    useCallback,
    useMemo,
    useRef,
    useEffect,
    useLayoutEffect,
    useState,
} from "react";
import {
    useMessages,
    useSendMessage,
    useMarkRead,
    messagesQueryKeyWithOrg,
    flattenMessagesPages,
} from "@api/chat.api";
import { useScope } from "@state/scopeContext.lib";
import {
    InfiniteScrollContainer,
    InfiniteScrollHandle,
} from "@design-system/InfiniteScrollContainer/InfiniteScrollContainer";
import { MessageType, Thread } from "@common/types/chat";
import { ThreadWithWorkspaceName } from "@common/schemas/chat.schema";
import { useActiveWorkspace } from "@hooks/useActiveWorkspace";
import { useNatsSubscription } from "@hooks/useNatsSubscription";
import { useChatTyping } from "@hooks/presence/useChatTyping";
import { TypingIndicator } from "@components/presence/TypingIndicator";
import { useQueryClient } from "@tanstack/react-query";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import {
    useSpreadsheetRefContextMaybe,
    useDraftRefsForTarget,
} from "@components/tasks/SpreadsheetRefContext";
import {
    useTransactionRefContextMaybe,
    useDraftTransactionRefsForTarget,
} from "@components/tasks/TransactionRefContext";
import {
    useCounterpartyRefContextMaybe,
    useDraftCounterpartyRefsForTarget,
} from "@components/tasks/CounterpartyRefContext";
import {
    useInvoiceRefContextMaybe,
    useDraftInvoiceRefsForTarget,
} from "@components/tasks/InvoiceRefContext";
import {
    useDocumentRefContextMaybe,
    useDraftDocumentRefsForTarget,
} from "@components/tasks/DocumentRefContext";
import { useChatComposerDrop } from "@components/tasks/chatDrag/useChatComposerDrop";
import { MessageFormSubmitParams, MessageForm } from "./MessageForm";
import styles from "./Chat.module.scss";
import { MessageBubble } from "./MessageBubble";
import { SubthreadPanel } from "./SubthreadPanel";
import { useChat } from "./Chat.context";
import { ChatThreadHeader } from "./ChatThreadHeader";

interface ChatProps {
    selectedThread: ThreadWithWorkspaceName;
    setSelectedThread: (thread?: Thread) => void;
    hideHeader?: boolean;
    titleLink?: string;
    isOrganizationChat?: boolean;
    isDisabled?: boolean;
    autoFocusInput?: boolean;
    customCommandIslandComponent?: React.ReactNode;
    /** Called when the user selects the /task slash command */
    onCreateTask?: () => void;
    /** False while a parent keeps the chat mounted but hidden; showing it scrolls to the newest message. */
    isVisible?: boolean;
}

export const Chat = ({
    selectedThread,
    setSelectedThread,
    hideHeader,
    titleLink,
    isOrganizationChat = false,
    isDisabled = false,
    autoFocusInput = true,
    customCommandIslandComponent = null,
    onCreateTask,
    isVisible = true,
}: ChatProps) => {
    const { close } = useChat();
    const toaster = useToaster();
    const plRefCtx = useSpreadsheetRefContextMaybe();
    const trxRefCtx = useTransactionRefContextMaybe();
    const cpRefCtx = useCounterpartyRefContextMaybe();
    const invRefCtx = useInvoiceRefContextMaybe();
    const docRefCtx = useDocumentRefContextMaybe();
    const mainDraftRefs = useDraftRefsForTarget("main");
    const mainDraftTransactionRefs = useDraftTransactionRefsForTarget("main");
    const mainDraftCounterpartyRefs = useDraftCounterpartyRefsForTarget("main");
    const mainDraftInvoiceRefs = useDraftInvoiceRefsForTarget("main");
    const mainDraftDocumentRefs = useDraftDocumentRefsForTarget("main");
    const mainComposerDrop = useChatComposerDrop("main");
    const workspace = useActiveWorkspace();
    const [activeSubthreadRootMessageId, setActiveSubthreadRootMessageId] =
        useState<string | null>(null);
    const { organizationId, activeWorkspaceKey } = useScope();
    const scrollContainerRef = useRef<InfiniteScrollHandle>(null);
    const queryClient = useQueryClient();

    // Reset subthread panel when the active thread changes
    useEffect(() => {
        setActiveSubthreadRootMessageId(null);
    }, [selectedThread.id]);

    useNatsSubscription(
        workspace?.id && !isOrganizationChat
            ? `workspaces.${workspace.id}.notifications.threads.>`
            : null,
        {
            onMessage: () => {
                if (!workspace?.id) {
                    return;
                }
                queryClient.invalidateQueries({
                    queryKey: messagesQueryKeyWithOrg(
                        activeWorkspaceKey,
                        selectedThread.id,
                        organizationId,
                    ),
                });
            },
        },
    );

    const { typingUserIds, notifyTyping } = useChatTyping(
        isOrganizationChat ? null : selectedThread.id,
    );

    const { mutate: markRead } = useMarkRead();
    useEffect(() => {
        markRead({
            threadId: selectedThread.id,
        });
    }, [selectedThread.id, markRead]);

    const {
        data: messagesPages,
        fetchNextPage: fetchNextMessages,
        hasNextPage: hasMoreMessages,
        isFetchingNextPage: isFetchingMoreMessages,
    } = useMessages(selectedThread.id);

    const messages = useMemo(
        () => flattenMessagesPages(messagesPages?.pages),
        [messagesPages],
    );

    useLayoutEffect(() => {
        if (isVisible && messages.length > 0) {
            scrollContainerRef.current?.scrollToBottom();
        }
    }, [isVisible, messages.length]);

    const { mutateAsync: sendMessage } = useSendMessage();
    const clearDraftRefs = plRefCtx?.clearDraftRefs;

    const onSend = useCallback(
        async ({
            content,
            organizationId: messageOrganizationId,
            spreadsheetReferences,
            transactionReferences,
            counterpartyReferences,
            invoiceReferences,
            documentReferences,
            financialDocumentIds,
        }: MessageFormSubmitParams) => {
            if (!selectedThread.id) {
                return;
            }
            try {
                await sendMessage({
                    threadId: selectedThread.id,
                    content,
                    type: MessageType.SLATE,
                    organizationId: messageOrganizationId,
                    spreadsheetReferences,
                    transactionReferences,
                    counterpartyReferences,
                    invoiceReferences,
                    documentReferences,
                    financialDocumentIds,
                });
                clearDraftRefs?.("main");
                trxRefCtx?.clearDraftRefs("main");
                cpRefCtx?.clearDraftRefs("main");
                invRefCtx?.clearDraftRefs("main");
                docRefCtx?.clearDraftRefs("main");
                scrollContainerRef.current?.scrollToBottom();
            } catch (error) {
                // Draft refs and attachment chips are intentionally preserved
                // on error so the user can retry without re-selecting cells or
                // re-uploading files. Re-throw so MessageForm keeps the composer.
                toaster.error("Failed to send message. Please try again.");
                throw error;
            }
        },
        [
            selectedThread.id,
            sendMessage,
            clearDraftRefs,
            trxRefCtx,
            cpRefCtx,
            invRefCtx,
            docRefCtx,
            toaster,
        ],
    );

    const handleClose = useCallback(() => {
        setSelectedThread(undefined);
        setActiveSubthreadRootMessageId(null);
        close();
    }, [setSelectedThread, close]);

    const handleReply = useCallback((messageId: string) => {
        setActiveSubthreadRootMessageId(messageId);
    }, []);

    return (
        <div className={styles.chatInnerContainer}>
            {!hideHeader && (
                <ChatThreadHeader
                    selectedThread={selectedThread}
                    isOrganizationChat={isOrganizationChat}
                    titleLink={titleLink}
                    onBack={() => setSelectedThread(undefined)}
                    onClose={handleClose}
                />
            )}
            <div className={styles.messagesContainer}>
                {messages.length > 0 ? (
                    <InfiniteScrollContainer
                        hasNextPage={hasMoreMessages}
                        ref={scrollContainerRef}
                        loadMore={fetchNextMessages}
                        isFetchingMoreMessages={isFetchingMoreMessages}
                        direction="up"
                        height="100%"
                    >
                        {messages.map((message) => (
                            <MessageBubble
                                key={message.id}
                                message={message}
                                onReply={handleReply}
                                taskId={selectedThread.taskId}
                            />
                        ))}
                    </InfiniteScrollContainer>
                ) : (
                    <div className={styles.emptyContainer}></div>
                )}
            </div>
            <div className={styles.chatInputContainer}>
                <TypingIndicator userIds={typingUserIds} />
                {customCommandIslandComponent}
                {!isDisabled && (
                    <MessageForm
                        onSubmit={onSend}
                        autoFocus={autoFocusInput}
                        placeholder="Leave a comment..."
                        draftRefs={mainDraftRefs.draftRefs}
                        onRemoveDraftRef={mainDraftRefs.onRemoveDraftRef}
                        draftTransactionRefs={
                            mainDraftTransactionRefs.draftRefs
                        }
                        onRemoveDraftTransactionRef={
                            mainDraftTransactionRefs.onRemoveDraftRef
                        }
                        draftCounterpartyRefs={
                            mainDraftCounterpartyRefs.draftRefs
                        }
                        onRemoveDraftCounterpartyRef={
                            mainDraftCounterpartyRefs.onRemoveDraftRef
                        }
                        draftInvoiceRefs={mainDraftInvoiceRefs.draftRefs}
                        onRemoveDraftInvoiceRef={
                            mainDraftInvoiceRefs.onRemoveDraftRef
                        }
                        draftDocumentRefs={mainDraftDocumentRefs.draftRefs}
                        onRemoveDraftDocumentRef={
                            mainDraftDocumentRefs.onRemoveDraftRef
                        }
                        onCreateTask={onCreateTask}
                        onTyping={notifyTyping}
                        resetKey={selectedThread.id}
                        isDropTarget={mainComposerDrop.isDropTarget}
                        focusRef={mainComposerDrop.focusRef}
                    />
                )}
            </div>
            {activeSubthreadRootMessageId && (
                <SubthreadPanel
                    rootMessageId={activeSubthreadRootMessageId}
                    threadId={selectedThread.id}
                    taskId={selectedThread.taskId}
                    onClose={() => setActiveSubthreadRootMessageId(null)}
                />
            )}
        </div>
    );
};
